import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const FormFTB3885 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="FTB Form 3885 | CA Corp Depreciation & Amortization Guide"
          description="Learn essential details on CA FTB 3885 for reporting depreciation and amortization. Ensure accurate filing and compliance with California tax laws."
          keywords=""
          canonical="resources/tax-exempt/ftb-form-3885/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 3885 </span>
          </span>{' '}
          - Corporation Depreciation and <br />
          Amortization
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/ftb-form-3885/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form FTB 3885 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Form FTB 3885F? Form FTB 3885F, Depreciation
                          and Amortization,
                        </strong>{' '}
                        is used to calculate
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          California depreciation and amortization deductions
                        </strong>{' '}
                        for corporations and entities classified as
                        corporations.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Uses It? Corporations, including partnerships and
                          LLCs classified as corporations,
                        </strong>{' '}
                        use FTB 3885 when California depreciation or
                        amortization must be calculated under the applicable
                        rules.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?
                        </strong>{' '}
                        FTB 3885 calculates{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          depreciation, amortization, and applicable IRC Section
                          179 expenses,{' '}
                        </strong>
                        including California-specific depreciation adjustments.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Supported Tax Returns:
                        </strong>{' '}
                        For{' '}
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/ftb-ca-form-199-filing/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 199
                        </Link>
                        , exempt organizations generally use{' '}
                        <strong style={{ fontWeight: 600 }}>FTB 3885 </strong>
                        when organized as corporations, while trusts use{' '}
                        <strong style={{ fontWeight: 600 }}>FTB 3885F</strong>.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Form FTB 3885?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Form FTB 3885?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howto" sx={{ color: '#0202ff' }}>
                          How to file Form 3885 with Taxzerone
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#source" sx={{ color: '#0202ff' }}>
                          Source and Reference
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="what"
                >
                  What is Form FTB 3885?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 3885 is used to calculate California depreciation and
                  amortization deduction for corporations, including
                  partnerships and limited liability companies (LLCs) classified
                  as corporations. This form must be filed by corporations
                  seeking to claim depreciation or amortization deductions on
                  their California state tax return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Filing this form ensures corporations can accurately account
                  for the wear and tear on their assets, reducing their taxable
                  income.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="who"
                >
                  Who must file Form FTB 3885?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Corporations operating in California that wish to report
                  deductions for the depreciation or amortization of their
                  business assets must file Form FTB 3885.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This form is typically required if the corporation has
                  purchased or improved physical or intangible assets during the
                  year, such as:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 16 }}>
                    Office equipment, machinery, or vehicles.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    {' '}
                    Buildings or real estate improvements.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Patents, copyrights, or other intellectual property.
                  </li>
                </ul>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> If your
                  corporation is claiming depreciation or amortization
                  deductions on its California return, you must complete and
                  submit this form to support those claims.
                </Alert>

                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0 }}
                      >
                        Streamline Your Form 3885 Filing with TaxZerone!
                      </Typography>
                      <Typography variant="body2">
                        Make your e-filing process simple by clicking the button
                        below.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 130, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=ftb3885`}
                      >
                        Get Started
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                {/* <Alert severity="warning" icon={false} sx={{ mb: 4 }} >
                  <strong style={{fontWeight:600}} >Note:</strong> The deadline is extended to the next business day
                  if the due date falls on a weekend or a legal holiday.
                </Alert> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="howto"
                >
                  How to file Form 3885 with Taxzerone
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3885-ftb-img.webp" />
                  <source srcSet="/assets/images/f3885-ftb-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3885-ftb-img.png"
                    loading="lazy"
                    alt="California Form FTB 3885 header area requiring corporation legal name and California corporation number."
                    width={760}
                    height={128}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter your corporation’s legal name and California corporation
                  number exactly as registered to ensure accurate and timely
                  processing of Form FTB 3885.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 22, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Part I Election to expense certain property under IRC Section
                  179
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3885-part1-img.webp" />
                  <source srcSet="/assets/images/f3885-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3885-part1-img.png"
                    loading="lazy"
                    alt="Form FTB 3885 Part I Section 179 deduction calculation with $25,000 California limit and $200,000 threshold."
                    width={760}
                    height={277}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the maximum Section 179 deduction allowed in California.
                  (For 2025: $25,000)
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add up the cost of all qualified Section 179 property placed
                  in service this year, including listed property. Only include
                  business-use cost.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the cost threshold before reduction. (For 2025:
                  $200,000)
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 3 from line 2. If the result is zero or less,
                  enter 0.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 4 from line 1. If zero or less, enter 0. If
                  zero, skip lines 6–11 and enter 0 on line 12.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  List the property you’re electing to expense (not listed
                  property).
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Column (a):</span> Brief
                    property description.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Column (b):</span> Cost
                    (business use only, excluding trade-in value).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Column (c):</span> Amount
                    you choose to expense. You don’t have to expense the full
                    cost.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Include listed property (vehicles under 6,000 lbs,
                  motorcycles, SUVs, or entertainment/photographic equipment)
                  here.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Exceptions: </span>{' '}
                    Equipment used 100% for business, computers, ambulances, or
                    vehicles for hire.
                  </li>
                  <li>Property used ≤50% for business doesn’t qualify.</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add lines 6(c) and 7.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 9
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the smaller line 5 or line 8.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 10
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any disallowed Section 179 deduction from previous
                  years.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 11
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the smaller business income or line 5. Business income =
                  net income from active trade/business (Form 100/100W, Side 2,
                  line 17), before this deduction.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 12
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add lines 9 and 10, but do not exceed line 11.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 13
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Subtract line 12 from the total of lines 9 + 10. This is the
                  amount you can carry forward to 2026.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 22, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Part II Depreciation and election of additional first-year
                  depreciation deduction under R&TC Section 24356
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3885-part2-img.webp" />
                  <source srcSet="/assets/images/f3885-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3885-part2-img.png"
                    loading="lazy"
                    alt="FTB 3885 Part II section showing Depreciation and Election of Additional First Year Depreciation Deduction under R&TC Section 24356"
                    width={760}
                    height={162}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 14
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Column (a) Description of property
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Name or type of asset (e.g., machinery, equipment, furniture).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Column (b) Date acquired
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  When the asset was purchased (format: mm/dd/yyyy).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Column (c) Cost or other basis
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The amount you paid for the asset or its adjusted value for
                  tax purposes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Column (d) Depreciation allowed or allowable in earlier years
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  How much depreciation has already been claimed on this asset
                  in previous years.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Column (e) Depreciation method
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The method you use to calculate depreciation (e.g.,
                  straight-line, declining balance).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Column (f) Life or rate
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Useful life of the asset in years, or the depreciation rate.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Column (g) Depreciation for this year
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The amount of regular depreciation claimed for the current
                  year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Column (h) Additional first-year depreciation
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Optional extra deduction for certain “qualifying property,” up
                  to a maximum of $2,000.M
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Line 15
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add the amounts in column (g) and column (h) for each asset or
                  group. Make sure the additional first-year depreciation in
                  column (h) does not exceed $2,000. Enter the total on Line 15.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 22, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Part III Summary
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3885-part3-img.webp" />
                  <source srcSet="/assets/images/f3885-part3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3885-part3-img.png"
                    loading="lazy"
                    alt="FTB 3885 Part III Summary section"
                    width={760}
                    height={150}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Line 16
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Complete this line based on the election made:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>
                    If electing IRC Section 179 expense: <br />
                    Add line 12 + line 15, column (g).
                  </li>
                  <li>
                    If claiming Additional First-Year Depreciation (R&TC Section
                    24356): <br />
                    Add line 15, column (g) + column (h).
                  </li>
                  <li>
                    If no election is made:
                    <br />
                    Enter line 15, column (g) only.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Line 17
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total depreciation claimed for federal purposes from
                  Federal Form 4562, line 22.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Line 18
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Compare line 17 and line 16:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>
                    If line 17 is greater than line 16,
                    <br />
                    Enter the difference here and on Form 100 or Form 100W, Side
                    1, line 6.
                  </li>
                  <li>
                    If line 17 is less than line 16,
                    <br />
                    Enter the difference here and on Form 100 or Form 100W, Side
                    2, line 12.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 22, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Part IV Amortization
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3885-part4-img.webp" />
                  <source srcSet="/assets/images/f3885-part4-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3885-part4-img.png"
                    loading="lazy"
                    alt="FTB 3885 Part IV Amortization section with amortization calculation and adjustment details"
                    width={760}
                    height={190}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Line 19
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (a) – Description of property
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the name or type of intangible property being amortized
                  (e.g., goodwill, start-up costs).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (b) – Date acquired (mm/dd/yyyy )
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the date the property was acquired.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (c) – Cost or other basis
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total cost or adjusted basis of the property.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (d) – Amortization allowed or allowable in earlier
                  years
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total amortization claimed in prior years.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (e) – R&TC Section
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the applicable California Revenue & Taxation Code
                  section under which the amortization is claimed.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (f) – Period or percentage
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amortization period (number of years) or the
                  applicable amortization rate.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (g) – Amortization for this year
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amortization deduction for the current taxable year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 20
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add the amounts in{' '}
                  <span style={{ fontWeight: 600 }}> column (g)</span> and enter
                  the total.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 21
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total amortization claimed for federal purposes from{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Federal Form 4562, line 44
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 22
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    If{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      line 21 is greater than line 20,
                    </span>{' '}
                    enter the difference here and on{' '}
                    <span style={{ fontWeight: 600 }}>
                      Form 100 or Form 100W, Side 1, line 6.
                    </span>
                  </li>
                  <li>
                    If{' '}
                    <span style={{ fontWeight: 600 }}>
                      line 21 is less than line 20,
                    </span>{' '}
                    enter the difference here and on{' '}
                    <span style={{ fontWeight: 600 }}>
                      Form 100 or Form 100W, Side 2, line 12.
                    </span>
                  </li>
                </Box>
                <Alert
                  severity="info"
                  icon={false}
                  sx={{ borderRadius: 2, mb: 3, scrollMarginTop: 88 }}
                  id="source"
                >
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                    id="add"
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        scrollMarginTop: 88,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        This content is based on official California{' '}
                        <Link
                          href="https://www.ftb.ca.gov/forms/2025/2025-3885-instructions.html"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          FTB Instructions for Form 3885
                        </Link>{' '}
                        (Corporation Depreciation and Amortization). For
                        complete and updated information, refer to the official
                        California Franchise Tax Board website.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Need to e-file California Form 3885?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Ensure your exempt organization accurately reports depreciation
              and amortization under FTB requirements.{' '}
              <Hidden smDown>
                <br />
              </Hidden>{' '}
              TaxZerone helps you calculate California depreciation adjustments
              and seamlessly attach
              <Hidden smDown>
                <br />
              </Hidden>{' '}
              Form 3885 to your{' '}
              <Link
                href="/tax-exempt/efile-ftb-ca-form199-online/"
                sx={{ color: '#0202ff' }}
              >
                {' '}
                Form 199
              </Link>
              -quickly, securely, and with confidence.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=ftb3885`}
              >
                E-file Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default FormFTB3885;
